import { ReactNode, useCallback, useEffect, useMemo } from 'react';

import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import type { MultipleChoiceAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  ACTIVITIES_ANSWER_MODE_FREE,
  ACTIVITIES_ANSWER_MULTIPLE_CHOICE_TYPE,
  LESSON_ACTIVITIES,
} from 'global/constants';
import customNanoId from 'lib/nanoid/nanoid';
import { useBatchAnswersContext } from '../shared/batch-answers.context';
import { getComponentSelectorId } from '../shared/logic/get-component-selector-id';
import SubActivityView from '../sub-activity/sub-activity-view';
import MultipleChoiceAnswerTeacher from './multiple-choice-answer-teacher';
import MultipleChoiceCheckAnswerFreeTeacher from './multiple-choice-check-answer-free-teacher';
import MultipleChoiceSelectedAnswers from './multiple-choice-selected-answers';
import { ViewProps } from './multiple-choice.constants';
import ComponentOverlay from '../shared/component-overlay/component-overlay';
import { ComponentOverlayWrapper } from '../shared/component-overlay/component-overlay.style';
import { setComponentMultiAnswer } from 'approot/activity-builder/check-answer/check-answer.utils';
import { checkAnswerVar } from 'approot/activity-builder/check-answer/check-answer.apollo';
import { getSubActivityInstructionAsText } from '../sub-activity/sub-activity.utils';
import MultipleChoiceViewOptions from './multiple-choice-view-options';
import { getMultiChoiceStudentAnswerCount } from 'approot/activity-builder/classroom/multiple-choice/multiple-choice-individual-response.utils';

const MultipleChoiceView = (props: ViewProps & { banner?: ReactNode }) => {
  const { subActivityProperties, id, properties, sectionId } = props;

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { isZoomToFit, isPublished, contentType } =
    useActivityBuilderPresentContext();
  const canRespond = isPublished && activityBuilderRespondFlag;

  const { currentAnswerData } = useCheckAnswer({
    componentId: id,
  });

  const isLesson = useMemo(
    () => contentType === LESSON_ACTIVITIES,
    [contentType]
  );

  const { multipleChoice } = properties;
  const activityId = subActivityProperties?.activityComponentId;
  const isFreeAnswer =
    properties.multipleChoice.answerMode === ACTIVITIES_ANSWER_MODE_FREE;

  const selectedAnswers = useMemo(
    () => currentAnswerData?.multiAnswer?.answerRefs || [],
    [currentAnswerData?.multiAnswer?.answerRefs]
  );
  const correctAnswers = useMemo(
    () => currentAnswerData?.multiAnswer?.correctRefs || [],
    [currentAnswerData?.multiAnswer?.correctRefs]
  );

  const { variables, componentAnswers, hasFetchedAnswers, error } =
    useTeacherGetAnswers({
      componentId: id,
      skip: !activityBuilderRespondFlag,
    });

  const { handleUpdate } = useBatchAnswersContext();

  const handleSubmitAnswer = useCallback(() => {
    if (activityId && isPublished && activityBuilderRespondFlag && variables) {
      handleUpdate({
        variables,
        input: {
          __typename: 'ActivityAnswer',
          uuid: customNanoId(),
          activityId,
          answerType: ACTIVITIES_ANSWER_MULTIPLE_CHOICE_TYPE,
          componentId: id,
          createdAt: new Date(),
          updatedAt: new Date(),
          studentId: null,
          answerReferences: null,
          answer: {
            __typename: 'MultipleChoiceAnswer',
            multipleChoiceItems: multipleChoice.options.map(opt => ({
              __typename: 'MultipleChoiceAnswerItem',
              itemId: opt.itemId || '',
              isSelected: selectedAnswers.includes(opt.itemId!),
              isCorrect: isFreeAnswer
                ? null
                : correctAnswers.includes(opt.itemId!) &&
                  opt.correctAnswerBoolean,
            })),
          },
        },
      });
    }
  }, [
    id,
    selectedAnswers,
    correctAnswers,
    multipleChoice,
    isFreeAnswer,
    activityId,
    isPublished,
    activityBuilderRespondFlag,
    variables,
    handleUpdate,
  ]);

  // if there is current answer data pull out the multiAnswer otherwise show empty
  const multiAnswerData =
    currentAnswerData?.hasChecked &&
    currentAnswerData?.multiAnswer?.answerRefs &&
    currentAnswerData.multiAnswer.answerRefs.length > 0
      ? (componentAnswers?.[0]?.answer as MultipleChoiceAnswerFragment)
      : undefined;

  useEffect(() => {
    if (
      !currentAnswerData?.hasReset &&
      !currentAnswerData?.multiAnswer?.answerRefs?.length
    ) {
      const multiAnswer = (
        componentAnswers?.[0]?.answer as MultipleChoiceAnswerFragment
      )?.multipleChoiceItems;

      if (multiAnswer) {
        setComponentMultiAnswer({
          sectionId: sectionId,
          componentId: id,
          answers: multiAnswer
            .filter(answer => answer.isSelected)
            .map(answer => answer.itemId),
          checkAnswerData: checkAnswerVar(),
          hasChecked: true,
        });
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentAnswers?.length, currentAnswerData, id, sectionId]);

  return (
    <SubActivityView subActivityDetails={subActivityProperties}>
      {isVisible && !isFreeAnswer && (
        <MultipleChoiceAnswerTeacher
          {...props}
          isPublished={isPublished}
          onClose={closeModal}
          onSubmitAnswer={handleSubmitAnswer}
        />
      )}

      {isVisible && isFreeAnswer && (
        <MultipleChoiceCheckAnswerFreeTeacher
          {...props}
          isPublished={isPublished}
          onClose={closeModal}
          onSubmitAnswer={handleSubmitAnswer}
        />
      )}

      <ComponentOverlayWrapper id={getComponentSelectorId(id)}>
        {canRespond ? (
          <>
            <MultipleChoiceSelectedAnswers
              {...props}
              isResponsive={!isZoomToFit}
              showAnswer={isLesson}
              selectedAnswers={multiAnswerData}
            />
            <ComponentOverlay
              ariaLabel={`Select your answer to: ${getSubActivityInstructionAsText(
                subActivityProperties?.instructions
              )}`}
              componentId={id}
              hasFetchedAnswers={hasFetchedAnswers}
              error={error}
              onClick={activityBuilderRespondFlag ? openModal : undefined}
              getActiveStudentAnswerCount={answers =>
                getMultiChoiceStudentAnswerCount(answers)
              }
            />
          </>
        ) : (
          <MultipleChoiceViewOptions
            {...props}
            isResponsive={!isZoomToFit}
            showAnswer={isLesson}
            onSelectOption={openModal}
          />
        )}
      </ComponentOverlayWrapper>
    </SubActivityView>
  );
};

export default MultipleChoiceView;
